import React, { useMemo } from 'react';

import { format, parseISO } from 'date-fns';

// components
import LoadingSpinner from '../../components/loading/nestedSpinner';

// hooks
import useHandleQueryParams from 'hooks/helper/useHandleQueryParams';

// constants
import {
    ActivtyLog,
    AuditTrailTimeframeOptions,
} from 'models/activty-log.model';

import AuditTrailView from './AuditTrailView';

type AuditTrailViewWrapperProps = {
    data: ActivtyLog[];
    isLoading: boolean;
};

const AuditTrailViewWrapper: React.FC<
    React.PropsWithChildren<AuditTrailViewWrapperProps>
> = ({ data, isLoading }) => {
    const [queryParams] = useHandleQueryParams();

    const {
        sortedDates,
        groupedLogs,
    }: {
        sortedDates: string[];
        groupedLogs: Record<string, ActivtyLog[]>;
    } = useMemo(() => {
        let filteredData = data ?? [];

        if (queryParams.person && queryParams.person !== 'all') {
            filteredData = filteredData.filter(
                (log) => String(log.user_id) === queryParams.person,
            );
        }

        if (
            queryParams.timeframe &&
            queryParams.timeframe !== AuditTrailTimeframeOptions.LIFETIME
        ) {
            const now = new Date();
            let cutoff: Date;

            if (queryParams.timeframe === AuditTrailTimeframeOptions.MONTH) {
                cutoff = new Date(now);
                cutoff.setDate(now.getDate() - 30);
            } else if (
                queryParams.timeframe === AuditTrailTimeframeOptions.WEEK
            ) {
                cutoff = new Date(now);
                cutoff.setDate(now.getDate() - 7);
            }

            filteredData = filteredData.filter(
                (log) => new Date(log.created_at) >= cutoff,
            );
        }

        const grouped: Record<string, ActivtyLog[]> = (
            filteredData ?? []
        ).reduce((acc, log) => {
            const dateKey = format(parseISO(log.created_at), 'dd MMMM yyyy');
            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push(log);
            return acc;
        }, {} as Record<string, ActivtyLog[]>);

        const sorted = Object.keys(grouped).sort((a, b) => {
            return new Date(b).getTime() - new Date(a).getTime();
        });

        return { sortedDates: sorted, groupedLogs: grouped };
    }, [data, queryParams]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <AuditTrailView sortedDates={sortedDates} groupedLogs={groupedLogs} />
    );
};

export default AuditTrailViewWrapper;
